import React, { useState, useEffect } from "react";
import { Box, X, Check, Tag, Layers, Sparkles } from "lucide-react";
import './AddPackage.css';
import axios from 'axios';
import ENDPOINTS, { API_BASE_URL } from '../../../Constants/ApiEndpoints';
import { toast } from 'react-toastify';
import { useAuth } from '../../../UserAccounts/AuthProvider';

const AddPackage = ({ isOpen, onClose, appointmentId, admissionId, onSuccess }) => {
    const { userData } = useAuth();
    const [packages, setPackages] = useState([]);
    const [loadingPackages, setLoadingPackages] = useState(false);

    const [selectedPackage, setSelectedPackage] = useState(null);
    const [sessions, setSessions] = useState(1);
    const [discount, setDiscount] = useState(0);
    const [totalCharge, setTotalCharge] = useState(0);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (isOpen) {
            fetchPackages();
        }
    }, [isOpen]);

    const fetchPackages = async () => {
        setLoadingPackages(true);
        try {
            const res = await axios.get(`${API_BASE_URL}/getAllPackages`);
            setPackages(res.data || []);
        } catch (err) {
            console.error("Error fetching packages:", err);
            // setPackages([
            //     { id: 1, packageName: "Basic Health Package", unitPrice: 500, packageDescription: "General Checkup & Blood Test" },
            //     { id: 2, packageName: "Full Body Checkup", unitPrice: 1500, packageDescription: "Complete Diagnostics" },
            //     { id: 3, packageName: "Cardiac Care Package", unitPrice: 2500, packageDescription: "ECG, Echo & Specialist Consultation" },
            // ]);
        } finally {
            setLoadingPackages(false);
        }
    };

    const handlePackageChange = (e) => {
        const pkg = packages.find(p => String(p.id) === String(e.target.value) || p.packageName === e.target.value);
        setSelectedPackage(pkg || null);
        setSessions(1);
        setDiscount(0);
        setTotalCharge(pkg ? pkg.unitPrice * 1 : 0);
    };

    const handleSessionsChange = (e) => {
        const val = Number(e.target.value);
        setSessions(val);
        if (selectedPackage) {
            setTotalCharge(Math.max(0, selectedPackage.unitPrice * val - discount));
        }
    };

    const handleDiscountChange = (e) => {
        const val = Number(e.target.value);
        setDiscount(val);
        if (selectedPackage) {
            setTotalCharge(Math.max(0, selectedPackage.unitPrice * sessions - val));
        }
    };

    const handleSave = async () => {
        if (!selectedPackage) {
            toast.error("Please select a package.");
            return;
        }

        setSaving(true);
        const payload = [{
            appointmentId: appointmentId || null,
            admissionId: admissionId || null,
            invoiceName: `Package: ${selectedPackage.packageName}`,
            invoiceDescription: `${selectedPackage.packageDescription || selectedPackage.packageName} (${sessions} session${sessions > 1 ? 's' : ''}, Discount: ₹${discount})`,
            totalAmount: totalCharge,
            addedBy: userData?.username || 'Staff'
        }];

        try {
            await axios.post(`${ENDPOINTS.CREATE_INVOICES}`, payload);
            toast.success("Package invoice created successfully!");
            if (onSuccess) onSuccess();
            onClose();
        } catch (error) {
            console.error("Error creating package invoice:", error);
            toast.error("Failed to add package invoice.");
        } finally {
            setSaving(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="modern-modal-overlay" onClick={onClose}>
            <div className="modern-modal-card animated-modal" onClick={(e) => e.stopPropagation()}>
                <div className="modal-top-accent-bar" />

                {/* Header */}
                <div className="modern-modal-header">
                    <div className="modal-title-container">
                        <div className="modal-icon-badge blue-glow">
                            <Box size={20} className="icon-blue" />
                        </div>
                        <div>
                            <h3 className="modal-main-heading">Add Medical Package</h3>
                            <p className="modal-sub-heading">Select a treatment or diagnostic package for the patient</p>
                        </div>
                    </div>
                    <button className="modal-close-icon-btn" onClick={onClose} aria-label="Close">
                        <X size={16} />
                    </button>
                </div>

                {/* Body */}
                <div className="modern-modal-body">
                    {/* Select Package Field */}
                    <div className="modern-field-group">
                        <label className="field-label-text">
                            <span>Select Medical Package</span>
                            <span className="required-asterisk">*</span>
                        </label>
                        <div className="input-with-icon-wrapper">
                            <select
                                className="modern-form-select"
                                value={selectedPackage ? selectedPackage.id || selectedPackage.packageName : ""}
                                onChange={handlePackageChange}
                            >
                                <option value="" disabled>
                                    {loadingPackages ? "Loading available packages..." : "-- Select Package --"}
                                </option>
                                {packages.map(p => (
                                    <option key={p.id || p.packageName} value={p.id || p.packageName}>
                                        {p.packageName} — ₹{p.unitPrice}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>

                    {/* Included Description */}
                    {selectedPackage && selectedPackage.packageDescription && (
                        <div className="included-pkg-banner">
                            <Sparkles size={14} className="sparkle-icon" />
                            <span><strong>Included Services:</strong> {selectedPackage.packageDescription}</span>
                        </div>
                    )}

                    {/* Sessions & Discount Grid */}
                    <div className="form-grid-2col">
                        <div className="modern-field-group">
                            <label className="field-label-text">
                                <Layers size={13} className="label-icon" />
                                <span>Sessions</span>
                            </label>
                            <input
                                type="number"
                                min="1"
                                className="modern-form-input"
                                value={sessions}
                                onChange={handleSessionsChange}
                            />
                        </div>

                        <div className="modern-field-group">
                            <label className="field-label-text">
                                <Tag size={13} className="label-icon" />
                                <span>Discount (₹)</span>
                            </label>
                            <input
                                type="number"
                                min="0"
                                className="modern-form-input"
                                value={discount}
                                onChange={handleDiscountChange}
                            />
                        </div>
                    </div>

                    {/* Total Amount Payable Banner */}
                    <div className="modern-total-payable-card">
                        <div className="total-left-info">
                            <span className="total-card-title">Total Payable Amount</span>
                            <span className="total-card-sub">Includes package charges and applied discount</span>
                        </div>
                        <div className="total-card-amount">
                            ₹{totalCharge.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </div>
                    </div>
                </div>

                {/* Footer */}
                <div className="modern-modal-footer">
                    <button className="modern-btn-secondary" onClick={onClose} disabled={saving}>
                        Cancel
                    </button>
                    <button className="modern-btn-primary" onClick={handleSave} disabled={saving}>
                        <Check size={16} />
                        <span>{saving ? "Adding Package..." : "Add Package"}</span>
                    </button>
                </div>
            </div>
        </div>
    );
};

export default AddPackage;
